import type { CalEvent } from '../../shared/types';
import { api, errorMessage } from '../api';
import { addDays, formatDayHeading, formatLong, todayISO } from '../dates';
import { openEventDialog } from '../dialogs';
import { loadSettings, saveSettings, type RangeMode } from '../settings';
import { notifyChange, refreshData, state } from '../state';
import { confirmDialog, h, icon, toast } from '../ui';

type Palette = { bg: string; accent: string };
const ORANGE: Palette = { bg: 'hsl(32 95% 89%)', accent: 'hsl(28 90% 52%)' }; // offen
const GREEN: Palette = { bg: 'hsl(140 55% 88%)', accent: 'hsl(140 50% 38%)' }; // erledigt
const BLUE: Palette = { bg: 'hsl(208 90% 91%)', accent: 'hsl(212 80% 52%)' }; // aus ICS-Datei
const GRAY: Palette = { bg: 'hsl(220 9% 91%)', accent: 'hsl(220 9% 58%)' }; // Individualtermin

function colorFor(e: CalEvent): Palette {
  if (e.source === 'ics') return BLUE;
  if (e.individual) return GRAY;
  return e.doneBy ? GREEN : ORANGE;
}

function eventCard(e: CalEvent): HTMLElement {
  const { bg, accent } = colorFor(e);
  let lastClick = 0;

  const del = h(
    'button',
    {
      class: 'icon-btn danger',
      type: 'button',
      title: 'Termin löschen',
      'aria-label': 'Termin löschen',
      onclick: async (ev: Event) => {
        ev.stopPropagation();
        if (!(await confirmDialog(`Termin „${e.title}“ am ${formatLong(e.date)} wirklich löschen?`))) return;
        try {
          await api.deleteEvent(e.id);
          await refreshData();
          notifyChange();
          toast('Termin gelöscht');
        } catch (err) {
          toast(errorMessage(err), 'error');
        }
      },
    },
    icon('trash', 18),
  );

  return h(
    'div',
    {
      class: 'event',
      tabindex: '0',
      role: 'button',
      title: e.description ? `${e.description}\n\n(Doppelklick zum Bearbeiten)` : 'Doppelklick: Details anzeigen / bearbeiten',
      style: `--ev-bg:${bg};--ev-accent:${accent}`,
      // Doppelklick / Doppeltippen (funktioniert mit Maus und Touch)
      onclick: () => {
        const now = Date.now();
        if (now - lastClick < 400) {
          lastClick = 0;
          openEventDialog(e);
        } else {
          lastClick = now;
        }
      },
      onkeydown: (ev: KeyboardEvent) => {
        if (ev.key === 'Enter') openEventDialog(e);
      },
    },
    h(
      'div',
      { class: 'event-main' },
      h('div', { class: 'event-title' }, e.doneBy && e.source !== 'ics' ? `${e.title} (${e.doneBy})` : e.title),
      e.time || e.source === 'ics'
        ? h(
            'div',
            { class: 'event-meta' },
            e.time ? h('span', { class: 'event-time' }, e.time) : null,
            e.source === 'ics' ? h('span', { class: 'badge', title: 'Aus ICS-Datei importiert' }, 'ICS') : null,
          )
        : null,
    ),
    del,
  );
}

export function renderCalendar(root: HTMLElement): void {
  const settings = loadSettings();
  const today = todayISO();
  const from = settings.range === 'all' ? '0000-00-00' : settings.range === 'today' ? today : addDays(today, -7);

  const groups = new Map<string, CalEvent[]>();
  for (const e of state.events) {
    if (e.date < from) continue;
    let list = groups.get(e.date);
    if (!list) {
      list = [];
      groups.set(e.date, list);
    }
    list.push(e);
  }

  const rangeSel = h('select', { 'aria-label': 'Zeitraum' });
  const options: [RangeMode, string][] = [
    ['week', 'Ab 7 Tage zurück'],
    ['today', 'Nur ab heute'],
    ['all', 'Alle Termine'],
  ];
  for (const [value, label] of options) rangeSel.append(h('option', { value }, label));
  rangeSel.value = settings.range;
  rangeSel.addEventListener('change', () => {
    saveSettings({ ...loadSettings(), range: rangeSel.value as RangeMode });
    notifyChange();
  });

  root.append(h('div', { class: 'toolbar' }, rangeSel));

  const dates = Array.from(groups.keys()).sort();
  if (dates.length === 0) {
    root.append(h('p', { class: 'empty' }, 'Keine Termine im gewählten Zeitraum. Mit dem „+“-Knopf unten rechts einen Termin hinzufügen.'));
    return;
  }

  const grid = h('div', { class: 'days' });
  for (const date of dates) {
    const events = (groups.get(date) ?? []).sort(
      (a, b) => (a.time ?? '').localeCompare(b.time ?? '') || a.title.localeCompare(b.title, 'de'),
    );
    grid.append(
      h(
        'section',
        { class: `day${date === today ? ' today' : ''}` },
        h('header', { class: 'day-head' }, h('span', {}, formatDayHeading(date)), icon('calendar', 16)),
        h('div', { class: 'day-events' }, events.map(eventCard)),
      ),
    );
  }
  root.append(grid);
}
